import { Text, Localizer } from 'preact-i18n';
import { Link } from 'preact-router/match';
import cx from 'classnames';

import StatusBadge from '../components/StatusBadge';
import VersionLink, { VersionSentence } from '../components/VersionLink';
import { getGithubRepoUrl, getLocalizedText } from '../utils';
import { RequestStatus } from '../../../../../utils/consts';
import integrationText from '../integrationText.css';

const SubContainerRow = ({ container, language }) => {
  const running = container.status === 'running';
  return (
    <tr>
      <td>{container.name}</td>
      <td>
        {running ? (
          <span class="badge badge-success">
            <Text id="integration.externalIntegration.supervision.containerRunning" />
          </span>
        ) : (
          <span class="badge badge-secondary">
            <Text id="integration.externalIntegration.supervision.containerStopped" />
          </span>
        )}
      </td>
      <td>
        {(container.ports || [])
          .filter(port => port.host_port)
          .map(port =>
            port.browsable === false ? (
              <span class="badge badge-secondary mr-1">
                {getLocalizedText(port.label, language) || port.container_port}
                {' : '}
                {port.host_port}
              </span>
            ) : (
              // no href while the container is stopped: nothing listens on the host port yet
              <a
                class={cx('btn btn-sm btn-outline-primary mr-1', { disabled: !running })}
                href={
                  running ? `${window.location.protocol}//${window.location.hostname}:${port.host_port}` : undefined
                }
                target="_blank"
                rel="noopener noreferrer"
                aria-disabled={!running}
              >
                <i class="fe fe-external-link mr-1" />
                <Text id="integration.externalIntegration.supervision.openButton" />{' '}
                {getLocalizedText(port.label, language) || port.container_port}
              </a>
            )
          )}
      </td>
    </tr>
  );
};

const SupervisionCard = ({
  integration,
  language,
  actionStatus,
  actionError,
  updateResult,
  uninstallStatus,
  askingUninstall,
  executeAction,
  onAskUninstall,
  onCancelUninstall,
  onUninstall
}) => {
  const repoUrl = getGithubRepoUrl(integration.store_slug);
  const actionInProgress = actionStatus === RequestStatus.Getting;
  const subContainers = integration.containers || [];
  return (
    <div class="card">
      <div class="card-header">
        <h3 class="card-title">
          <Text id="integration.externalIntegration.supervision.title" />
        </h3>
        <div class="card-options">
          <StatusBadge status={integration.status} />
        </div>
      </div>
      <div
        class={cx('dimmer', {
          active: actionInProgress || uninstallStatus === RequestStatus.Getting
        })}
      >
        <div class="loader" />
        <div class="dimmer-content">
          <div class="card-body">
            {actionError && (
              <div class="alert alert-danger">
                <Text id="integration.externalIntegration.supervision.actionError" />
              </div>
            )}
            {uninstallStatus === RequestStatus.Error && (
              <div class="alert alert-danger">
                <Text id="integration.externalIntegration.supervision.uninstallError" />
              </div>
            )}
            {updateResult && (
              <div class={cx('alert', updateResult.upToDate ? 'alert-info' : 'alert-success')}>
                <VersionSentence
                  id={
                    updateResult.upToDate
                      ? 'integration.externalIntegration.supervision.alreadyUpToDateText'
                      : 'integration.externalIntegration.supervision.updateSuccessText'
                  }
                  fields={{ version: updateResult.version }}
                  storeSlug={integration.store_slug}
                  version={updateResult.version}
                />
              </div>
            )}
            {integration.update_available && (
              <div class="alert alert-info">
                {/* the version number is what tells the user what the update actually brings; it is
                    unknown only when the store index has not been read yet, hence the plain fallback */}
                {integration.latest_version ? (
                  // the offered version is the one "what changed?" is asked about, so it is the one
                  // linked to its changelog here; the installed number repeated in the sentence stays
                  // plain text, it is already a link in the details list below
                  <VersionSentence
                    id="integration.externalIntegration.supervision.updateAvailableVersionText"
                    fields={{ latestVersion: integration.latest_version, currentVersion: integration.version }}
                    storeSlug={integration.store_slug}
                    version={integration.latest_version}
                  />
                ) : (
                  <Text id="integration.externalIntegration.supervision.updateAvailableText" />
                )}
              </div>
            )}
            <dl class="row">
              <dt class="col-5 col-sm-4">
                <Text id="integration.externalIntegration.supervision.statusLabel" />
              </dt>
              <dd class="col-7 col-sm-8">
                <StatusBadge status={integration.status} />
              </dd>
              {integration.connection_status && [
                <dt class="col-5 col-sm-4">
                  <Text id="integration.externalIntegration.connection.label" />
                </dt>,
                <dd class="col-7 col-sm-8">
                  <span class={cx('badge', integration.connection_status.connected ? 'badge-success' : 'badge-danger')}>
                    {integration.connection_status.connected ? (
                      <Text id="integration.externalIntegration.connection.connectedBadge" />
                    ) : (
                      <Text id="integration.externalIntegration.connection.disconnectedBadge" />
                    )}
                  </span>
                  {integration.connection_status.message && (
                    <div class={cx('text-muted small', integrationText.integrationText)}>
                      {getLocalizedText(integration.connection_status.message, language)}
                    </div>
                  )}
                </dd>
              ]}
              {integration.started_at && [
                <dt class="col-5 col-sm-4">
                  <Text id="integration.externalIntegration.supervision.startedAtLabel" />
                </dt>,
                <dd class="col-7 col-sm-8">{new Date(integration.started_at).toLocaleString(language)}</dd>
              ]}
              {integration.version && [
                <dt class="col-5 col-sm-4">
                  <Text id="integration.externalIntegration.supervision.versionLabel" />
                </dt>,
                <dd class="col-7 col-sm-8">
                  <VersionLink storeSlug={integration.store_slug} version={integration.version} />
                </dd>
              ]}
              {integration.docker_image && [
                <dt class="col-5 col-sm-4">
                  <Text id="integration.externalIntegration.supervision.dockerImageLabel" />
                </dt>,
                <dd class="col-7 col-sm-8">
                  <code>{integration.docker_image}</code>
                </dd>
              ]}
              {repoUrl && [
                <dt class="col-5 col-sm-4">
                  <Text id="integration.externalIntegration.supervision.repoLabel" />
                </dt>,
                <dd class="col-7 col-sm-8">
                  <a href={repoUrl} target="_blank" rel="noopener noreferrer">
                    <i class="fe fe-github mr-1" />
                    {integration.store_slug}
                  </a>
                </dd>
              ]}
            </dl>

            {subContainers.length > 0 && (
              <div class="mb-4">
                <h4>
                  <Text id="integration.externalIntegration.supervision.containersTitle" />
                </h4>
                <div class="table-responsive">
                  <table class="table table-sm card-table">
                    <tbody>
                      {subContainers.map(container => (
                        <SubContainerRow container={container} language={language} />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div class="btn-list">
              <button
                class="btn btn-outline-success"
                onClick={() => executeAction('start')}
                disabled={actionInProgress}
              >
                <i class="fe fe-play mr-1" />
                <Text id="integration.externalIntegration.supervision.startButton" />
              </button>
              <button class="btn btn-outline-warning" onClick={() => executeAction('stop')} disabled={actionInProgress}>
                <i class="fe fe-square mr-1" />
                <Text id="integration.externalIntegration.supervision.stopButton" />
              </button>
              <button
                class="btn btn-outline-primary"
                onClick={() => executeAction('restart')}
                disabled={actionInProgress}
              >
                <i class="fe fe-rotate-cw mr-1" />
                <Text id="integration.externalIntegration.supervision.restartButton" />
              </button>
              {integration.update_available ? (
                <button class="btn btn-primary" onClick={() => executeAction('update')} disabled={actionInProgress}>
                  <i class="fe fe-arrow-up-circle mr-1" />
                  <Text id="integration.externalIntegration.supervision.updateButton" />
                </button>
              ) : (
                <Localizer>
                  <button
                    class="btn btn-outline-secondary"
                    onClick={() => executeAction('update')}
                    disabled={actionInProgress}
                    title={<Text id="integration.externalIntegration.supervision.forceUpdateTitle" />}
                  >
                    <i class="fe fe-download-cloud mr-1" />
                    <Text id="integration.externalIntegration.supervision.forceUpdateButton" />
                  </button>
                </Localizer>
              )}
              <Link
                href={`/dashboard/integration/device/external/${integration.selector}/logs`}
                class="btn btn-secondary"
              >
                <i class="fe fe-file-text mr-1" />
                <Text id="integration.externalIntegration.supervision.logsButton" />
              </Link>
              {!askingUninstall && (
                <button class="btn btn-danger" onClick={onAskUninstall}>
                  <i class="fe fe-trash-2 mr-1" />
                  <Text id="integration.externalIntegration.supervision.uninstallButton" />
                </button>
              )}
            </div>

            {askingUninstall && (
              <div class="alert alert-danger mt-4">
                <p>
                  <Text id="integration.externalIntegration.supervision.uninstallWarning" />
                </p>
                <div class="btn-list mb-0">
                  <button class="btn btn-danger" onClick={onUninstall}>
                    <i class="fe fe-trash-2 mr-1" />
                    <Text id="integration.externalIntegration.supervision.confirmUninstallButton" />
                  </button>
                  <button class="btn btn-secondary" onClick={onCancelUninstall}>
                    <Text id="integration.externalIntegration.supervision.cancelUninstallButton" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupervisionCard;
